import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../i18n/i18n';
import es from '../i18n/locales/es.json';
import en from '../i18n/locales/en.json';

export type Language = 'es' | 'en';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (path: string, fallback?: string) => string;
}

const dictionaries: Record<Language, any> = { es, en };

const LanguageContext = createContext<LanguageContextType>({
  lang: 'es',
  setLang: () => {},
  t: (path: string, fallback?: string) => fallback || path,
});

const resolveKey = (obj: any, key: string): any => {
  if (!obj || typeof obj !== 'object') return undefined;
  if (key in obj) return obj[key];
  const lowerKey = key.toLowerCase();
  for (const k of Object.keys(obj)) {
    if (k.toLowerCase() === lowerKey) return obj[k];
  }
  return undefined;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Language>(() => {
    const saved = localStorage.getItem('nexo_lang') || localStorage.getItem('kuphia_lang') || localStorage.getItem('i18nextLng');
    return saved && saved.startsWith('en') ? 'en' : 'es';
  });

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }

    const handleLangChanged = (lng: string) => {
      const normalized = lng.startsWith('en') ? 'en' : 'es';
      setLangState(normalized);
      localStorage.setItem('nexo_lang', normalized);
      localStorage.setItem('i18nextLng', normalized);
    };

    i18n.on('languageChanged', handleLangChanged);
    return () => {
      i18n.off('languageChanged', handleLangChanged);
    };
  }, [lang]);

  const setLang = (newLang: Language) => {
    setLangState(newLang);
    localStorage.setItem('nexo_lang', newLang);
    localStorage.setItem('i18nextLng', newLang);
    i18n.changeLanguage(newLang);
    window.dispatchEvent(new Event('languagechange'));
  };

  const t = (path: string, fallback?: string): string => {
    if (!path) return fallback || '';
    const keys = path.split('.');
    
    // Intentar idioma actual
    let current = dictionaries[lang];
    let matched = true;
    for (const key of keys) {
      current = resolveKey(current, key);
      if (current === undefined) {
        matched = false;
        break;
      }
    }
    if (matched && typeof current === 'string') return current;

    // Fallback a español si no se encontró
    let esCurrent = dictionaries['es'];
    let esMatched = true;
    for (const esKey of keys) {
      esCurrent = resolveKey(esCurrent, esKey);
      if (esCurrent === undefined) {
        esMatched = false;
        break;
      }
    }
    if (esMatched && typeof esCurrent === 'string') return esCurrent;

    return fallback || path;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
